/**
 * LegalDocModal — single source of truth for in-app legal document
 * viewing.
 *
 * Used by signup forms, settings, the admin Preview button, and the
 * re-acceptance gate.
 *
 * UX (per spec):
 *   • Desktop  : centred max-w 700px, max-h 80vh, scrollable body,
 *                sticky header + sticky footer, 24px padding.
 *   • Mobile   : full-screen, 16px padding, slide-up animation,
 *                same sticky header + footer.
 *   • Body uses the shared `.legal-document` typography (12px body,
 *     uppercase numbered section headings, purple bullets, etc.) —
 *     applied at render time, so admin-supplied HTML stays semantic.
 *   • Body opens with a centred document-header block (title +
 *     effective date + version + jurisdiction) followed by a divider.
 *   • Sticky footer shows "Please scroll to read the full document"
 *     on the left and the purple "I Understand" CTA on the right.
 */
import { useEffect, useState } from 'react';
import { X, FileText, Shield, Loader2 } from 'lucide-react';
import axios from 'axios';
import { API } from '@/App';
import './legal-document.css';

const META = {
  terms: { icon: FileText, label: 'Terms and Conditions' },
  privacy: { icon: Shield, label: 'Privacy Policy' },
};

// Hardcoded for now — Unexa is an Australian platform. Move to the
// `legal_documents` row if we ever ship to other jurisdictions.
const JURISDICTION = 'Australia';

function fmtDate(s) {
  if (!s) return '';
  try {
    return new Date(s).toLocaleDateString(undefined, {
      year: 'numeric', month: 'long', day: 'numeric',
    });
  } catch {
    return '';
  }
}

export default function LegalDocModal({ documentType, onClose, onUnderstand }) {
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    axios.get(`${API}/legal/${documentType}`)
      .then(r => { if (alive) setDoc(r.data); })
      .catch(() => { if (alive) setError('Failed to load document'); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [documentType]);

  // Lock body scroll while modal is open.
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, []);

  const meta = META[documentType] || META.terms;
  const Icon = meta.icon;
  const effectiveDate = fmtDate(doc?.effective_date);
  const publishedAt = fmtDate(doc?.published_at);

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-end sm:items-center justify-center bg-black/60 backdrop-blur-sm animate-in fade-in duration-150"
      data-testid={`legal-modal-${documentType}`}
      onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
    >
      <div
        className="bg-white w-full sm:w-[700px] sm:max-w-[92vw] h-full sm:h-auto sm:max-h-[80vh] sm:rounded-2xl shadow-2xl flex flex-col animate-in slide-in-from-bottom sm:slide-in-from-bottom-0 sm:zoom-in-95 duration-200"
        role="dialog"
        aria-modal="true"
        aria-labelledby={`legal-title-${documentType}`}
      >
        {/* Sticky header — simple title + close button (the rich
            centred title block lives inside the scrollable body so it
            scrolls away with the content). */}
        <header className="sticky top-0 bg-white border-b border-gray-200 px-4 sm:px-6 py-3 sm:rounded-t-2xl flex items-center justify-between gap-3 z-10">
          <div className="flex items-center gap-2.5 min-w-0">
            <div className="w-8 h-8 rounded-lg bg-indigo-50 text-indigo-600 flex items-center justify-center shrink-0">
              <Icon className="h-4 w-4" />
            </div>
            <h2
              id={`legal-title-${documentType}`}
              className="text-sm font-bold text-gray-900 truncate"
            >
              {meta.label}
            </h2>
          </div>
          <button
            onClick={() => onClose?.()}
            className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400 shrink-0"
            aria-label="Close"
            data-testid={`legal-modal-close-${documentType}`}
          >
            <X className="h-4 w-4" />
          </button>
        </header>

        {/* Scrollable body — 24px desktop / 16px mobile padding per spec */}
        <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-4 sm:py-6">
          {loading && (
            <div className="flex items-center justify-center py-16 text-gray-400 gap-2">
              <Loader2 className="h-5 w-5 animate-spin" /> Loading…
            </div>
          )}
          {error && (
            <div className="bg-red-50 border border-red-100 rounded-lg p-4 text-sm text-red-600">
              {error}
            </div>
          )}
          {!loading && !error && doc && (
            <>
              {/* Document-header block — centred, with divider */}
              <div className="legal-document">
                <div className="legal-doc-header">
                  <div className="doc-title">{meta.label}</div>
                  <div className="doc-meta">
                    {effectiveDate && <>Effective: <strong>{effectiveDate}</strong><br /></>}
                    {doc.version && <>Version: <strong>{doc.version}</strong><br /></>}
                    Jurisdiction: <strong>{JURISDICTION}</strong>
                    {publishedAt && <><br />Last updated: <strong>{publishedAt}</strong></>}
                  </div>
                </div>

                {/* Admin-supplied HTML rendered with the shared legal-document CSS */}
                <div
                  data-testid={`legal-content-${documentType}`}
                  dangerouslySetInnerHTML={{ __html: doc.content || '' }}
                />
              </div>
            </>
          )}
        </div>

        {/* Sticky footer — always visible, never scrolls. */}
        <footer className="sticky bottom-0 bg-white border-t border-gray-200 px-4 sm:px-6 py-3 sm:rounded-b-2xl flex items-center justify-between gap-3 z-10">
          <span className="text-[11px] text-gray-400 leading-tight hidden xs:block sm:block">
            Please scroll to read the full document
          </span>
          <button
            onClick={() => { onUnderstand ? onUnderstand() : onClose?.(); }}
            className="ml-auto px-5 py-2 rounded-lg bg-[#6366f1] hover:bg-[#4f46e5] text-white text-xs font-bold transition-colors"
            data-testid={`legal-modal-understand-${documentType}`}
          >
            I Understand
          </button>
        </footer>
      </div>
    </div>
  );
}
